import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const CONCURRENCY = 6; // simultaneous asset downloads
const ASSET_TIMEOUT_MS = 12000;

/*
 * Module-level image cache.
 * Keeps Image objects alive after PreparationScreen unmounts so the browser
 * memory cache is not GC'd before the user opens a question tile.
 * Cleared when a new game session starts.
 */
const _preloadedImageCache = new Map();

/* ── bounded concurrency asset preloader ── */
async function preloadAssets(urls, onProgress) {
  // Only preload real network URLs — skip base64 data: URLs (they don't need fetching)
  const unique = [...new Set(urls.filter(u => u && u.startsWith("http")))];
  let done = 0;
  const total = unique.length;
  if (total === 0) { onProgress(0, 0, 0); return; }

  const loadOne = (url) => new Promise((resolve) => {
    const isAudio = /\.(mp3|ogg|wav|aac|m4a)(\?|$)/i.test(url);
    let settled = false;
    const finish = () => { if (!settled) { settled = true; resolve(); } };
    const timer = setTimeout(finish, ASSET_TIMEOUT_MS);

    if (isAudio) {
      const a = new Audio();
      a.preload = "auto";
      a.oncanplaythrough = () => { clearTimeout(timer); finish(); };
      a.onerror = () => { clearTimeout(timer); finish(); };
      a.src = url;
    } else {
      // Store in module-level cache so the Image object stays alive after
      // PreparationScreen unmounts — prevents browser GC from evicting memory cache
      const img = new Image();
      _preloadedImageCache.set(url, img);
      img.onload  = () => { clearTimeout(timer); finish(); };
      img.onerror = () => { clearTimeout(timer); finish(); }; // non-fatal
      img.src = url;
      // decode() primes the decoded bitmap — image paints instantly on first render
      if (img.decode) img.decode().catch(() => {});
    }
  });

  // Process in chunks of CONCURRENCY
  for (let i = 0; i < unique.length; i += CONCURRENCY) {
    const chunk = unique.slice(i, i + CONCURRENCY);
    await Promise.all(chunk.map(loadOne));
    done = Math.min(done + chunk.length, total);
    onProgress(done, total, Math.round((done / total) * 100));
  }
}

/* ── build asset list from manifest ── */
function buildAssetList(manifest) {
  const images = [];
  const audio = [];
  if (!manifest) return { images, audio, total: 0 };
  for (const q of Object.values(manifest)) {
    // Only include real network URLs — base64 data: URLs are inline and need no preloading.
    // Including them wastes memory, consumes CPU for decoding, and can push real
    // Cloudinary images out of the browser's limited memory cache on mobile.
    if (q.image_url && q.image_url.startsWith("http"))               images.push(q.image_url);
    if (q.answer_image_url && q.answer_image_url.startsWith("http")) images.push(q.answer_image_url);
    if (q.audio_url && q.audio_url.startsWith("http"))               audio.push(q.audio_url);
  }
  return {
    images: [...new Set(images)],
    audio:  [...new Set(audio)],
    total:  new Set([...images, ...audio]).size,
  };
}

/* ── phase labels ── */
const PHASES = [
  "اختيار الأسئلة",
  "تجهيز الأسئلة",
  "تحميل الصور",
  "تحميل الأصوات",
  "المباراة جاهزة",
];

export default function PreparationScreen() {
  const navigate = useNavigate();
  const { session, userToken, saveManifest, gameMode, soloSetup, refreshUser, logoutUser } = useGame();

  const [phase, setPhase]       = useState(0); // 0-4
  const [error, setError]       = useState(null);
  const [manifest, setLocalManifest] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [imgProgress, setImgProgress] = useState({ done: 0, total: 0 });
  const [audioProgress, setAudioProgress] = useState({ done: 0, total: 0 });
  const prepared = useRef(false);

  const runPreparation = useCallback(async () => {
    if (prepared.current) return;
    prepared.current = true;
    setError(null);

    // Clear image cache from previous game so we don't accumulate stale Image objects
    _preloadedImageCache.clear();

    try {
      // ── Phase 0: selecting questions ──
      setPhase(0);
      const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
      const { data } = await axios.post(
        `${API}/game/prepare`,
        { session_id: session.id },
        { headers }
      );
      const m = data.manifest;
      setLocalManifest(m);
      setQuestionCount(data.total_slots);

      // ── Phase 1: questions ready ──
      setPhase(1);
      saveManifest(m, session.id);
      await new Promise(r => setTimeout(r, 400)); // let React render

      const assets = buildAssetList(m);

      // ── Phase 2: preload images ──
      setPhase(2);
      const allImages = [...assets.images];
      setImgProgress({ done: 0, total: allImages.length });
      if (allImages.length > 0) {
        await preloadAssets(allImages, (done, total) => {
          setImgProgress({ done, total });
        });
      }

      // ── Phase 3: preload audio ──
      setPhase(3);
      setAudioProgress({ done: 0, total: assets.audio.length });
      if (assets.audio.length > 0) {
        await preloadAssets(assets.audio, (done, total) => {
          setAudioProgress({ done, total });
        });
      }

      // ── Phase 4: ready ──
      setPhase(4);
      // Refresh user state so credit counter in UI reflects the deduction
      refreshUser().catch(() => {});
      await new Promise(r => setTimeout(r, 600));
      navigate("/game", { replace: true });
    } catch (err) {
      prepared.current = false; // allow retry
      if (err?.response?.status === 401) {
        // Token expired — log out and redirect to login so user can re-authenticate
        logoutUser();
        navigate("/login", { replace: true });
        return;
      }
      const msg = err?.response?.data?.detail || err?.message || "حدث خطأ أثناء التجهيز";
      setError(msg);
    }
  }, [session, userToken, saveManifest, navigate, refreshUser, logoutUser]);

  useEffect(() => {
    if (!session?.id) { navigate("/"); return; }
    runPreparation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── UI helpers ── */
  const C = {
    bg:     "#060302",
    card:   "rgba(201,168,76,0.07)",
    border: "rgba(201,168,76,0.22)",
    gold:   "#C9A84C",
    goldLt: "#E8C46A",
    cream:  "#F5EDD8",
    dim:    "rgba(245,237,216,0.40)",
  };

  const Step = ({ label, status, counter }) => {
    const icon = status === "done"    ? "✓"
               : status === "active"  ? "⟳"
               : "·";
    const col  = status === "done"    ? "#43e97b"
               : status === "active"  ? C.goldLt
               : C.dim;
    return (
      <div style={{ display:"flex", alignItems:"center", gap:"12px", padding:"9px 0" }}>
        <div style={{
          width:"28px", height:"28px", borderRadius:"50%", flexShrink:0,
          display:"flex", alignItems:"center", justifyContent:"center",
          background: status === "done" ? "rgba(67,233,123,0.15)" : status === "active" ? "rgba(201,168,76,0.15)" : "rgba(255,255,255,0.04)",
          border: `1.5px solid ${col}`,
          color: col, fontSize:"0.85rem", fontWeight:900,
          animation: status === "active" ? "prepSpin 1.2s linear infinite" : "none",
        }}>{icon}</div>
        <div style={{ flex:1 }}>
          <div style={{ color: status === "pending" ? C.dim : C.cream, fontWeight: status === "done" ? 700 : status === "active" ? 800 : 400, fontSize:"0.9rem" }}>
            {label}
          </div>
          {counter && (
            <div style={{ color: C.gold, fontSize:"0.72rem", fontWeight:700, marginTop:"2px" }}>{counter}</div>
          )}
        </div>
      </div>
    );
  };

  const stepStatus = (i) =>
    phase > i ? "done" : phase === i ? "active" : "pending";

  return (
    <div style={{
      minHeight:"100dvh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background: `linear-gradient(rgba(6,3,2,0.88),rgba(6,3,2,0.95)), url('https://images.pexels.com/photos/159862/art-school-of-athens-raphael-italian-painter-fresco-159862.jpeg?auto=compress&cs=tinysrgb&w=1280') center/cover no-repeat`,
      fontFamily:"'Cairo','Tajawal',sans-serif", direction:"rtl",
      padding:"24px",
    }}>
      <style>{`
        @keyframes prepSpin { to { transform: rotate(360deg); } }
        @keyframes prepPulse { 0%,100%{opacity:1;} 50%{opacity:0.55;} }
        @keyframes prepBar { from{width:0} to{width:100%} }
      `}</style>

      {/* Logo */}
      <div style={{ marginBottom:"32px", textAlign:"center" }}>
        <div style={{ fontSize:"2rem", fontWeight:900, background:`linear-gradient(135deg,${C.goldLt},${C.gold})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", letterSpacing:"0.04em" }}>
          حُجّة
        </div>
      </div>

      {/* Card */}
      <div style={{
        width:"min(420px,94vw)", background:C.card,
        border:`1.5px solid ${C.border}`,
        borderRadius:"20px", padding:"28px 24px",
        backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
        boxShadow:"0 20px 60px rgba(0,0,0,0.65)",
      }}>
        {/* Title */}
        <div style={{ marginBottom:"24px" }}>
          <div style={{ color:C.cream, fontWeight:900, fontSize:"1.1rem", marginBottom:"4px" }}>
            {phase < 4 ? "جاري تجهيز المباراة…" : "المباراة جاهزة ✓"}
          </div>
          <div style={{ color:C.dim, fontSize:"0.75rem" }}>
            {gameMode === "solo"
              ? (soloSetup?.playerName || session?.team1_name || "اللاعب")
              : `${session?.team1_name} ضد ${session?.team2_name}`}
          </div>
        </div>

        {/* Steps */}
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:"16px" }}>
          <Step
            label={PHASES[0]}
            status={stepStatus(0)}
          />
          <Step
            label={PHASES[1]}
            status={stepStatus(1)}
            counter={questionCount > 0 ? `${questionCount} سؤال جاهز` : null}
          />
          <Step
            label={PHASES[2]}
            status={stepStatus(2)}
            counter={imgProgress.total > 0 ? `${imgProgress.done} من ${imgProgress.total} صورة` : null}
          />
          <Step
            label={PHASES[3]}
            status={stepStatus(3)}
            counter={audioProgress.total > 0 ? `${audioProgress.done} من ${audioProgress.total} مقطع` : null}
          />
          <Step
            label={PHASES[4]}
            status={stepStatus(4)}
          />
        </div>

        {/* Error state */}
        {error && (
          <div style={{ marginTop:"20px", padding:"14px 16px", borderRadius:"12px", background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.35)" }}>
            <div style={{ color:"#fca5a5", fontWeight:700, fontSize:"0.85rem", marginBottom:"8px" }}>
              {error}
            </div>
            <button
              onClick={() => { prepared.current = false; runPreparation(); }}
              style={{
                background:"rgba(201,168,76,0.12)", border:`1px solid ${C.border}`,
                color:C.gold, borderRadius:"8px", padding:"7px 18px",
                fontSize:"0.82rem", fontWeight:800, cursor:"pointer",
                fontFamily:"'Cairo','Tajawal',sans-serif",
              }}
            >
              إعادة المحاولة
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
